import React, { useState, useEffect, useRef } from 'react';
import { withRouter } from 'react-router-dom';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { useDispatch, useSelector } from 'react-redux';
import { doLogin } from './authSlice';

// ─── Design tokens (mirrored from RegisterPage) ───────────────────────────────
const tokens = {
  navy:        "#0A1628",
  accent:      "#2563EB",
  accentHover: "#1D4ED8",
  border:      "#E2E8F0",
  surface:     "#F8FAFC",
  white:       "#FFFFFF",
  danger:      "#DC2626",
  textPrimary:   "#0F172A",
  textSecondary: "#475569",
};

// ─── Global styles ────────────────────────────────────────────────────────────
const GlobalStyles = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');

    *, *::before, *::after { box-sizing: border-box; }

    .login-root {
      display: flex;
      min-height: 100vh;
      font-family: 'Inter', system-ui, sans-serif;
      background: ${tokens.surface};
    }

    /* ── Left panel (identical to RegisterPage) ── */
    .login-panel {
      width: 380px;
      flex-shrink: 0;
      background: ${tokens.navy};
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      padding: 48px 40px;
      position: relative;
      overflow: hidden;
    }
    .login-panel::before {
      content: '';
      position: absolute;
      inset: 0;
      background:
        radial-gradient(ellipse 600px 500px at -100px 120%, #2563EB22 0%, transparent 70%),
        radial-gradient(ellipse 400px 400px at 420px 500px, #2563EB18 0%, transparent 70%);
      pointer-events: none;
    }
    .login-panel-logo img {
      width:170px;
    }
    .login-panel-badge {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      background: rgba(37,99,235,0.18);
      border: 1px solid rgba(37,99,235,0.35);
      border-radius: 20px;
      padding: 4px 12px;
      font-size: 11px;
      font-weight: 600;
      letter-spacing: .08em;
      text-transform: uppercase;
      color: #93C5FD;
      margin-bottom: 20px;
    }
    .login-panel-headline {
      font-size: 28px;
      font-weight: 700;
      line-height: 1.3;
      color: ${tokens.white};
      margin: 0 0 16px;
    }
    .login-panel-quote {
      font-size: 14px;
      line-height: 1.7;
      color: rgba(255,255,255,0.65);
      border-left: 2px solid ${tokens.accent};
      padding-left: 16px;
      margin: 0 0 40px;
    }
    .login-panel-stats {
      display: flex;
      gap: 32px;
    }
    .login-stat-num {
      font-size: 24px;
      font-weight: 700;
      color: ${tokens.white};
    }
    .login-stat-label {
      font-size: 11px;
      color: rgba(255,255,255,0.5);
      margin-top: 2px;
      letter-spacing: .04em;
    }
    .login-panel-img {
      width: 100%;
      border-radius: 12px;
      overflow: hidden;
      margin: 32px 0;
    }
    .login-panel-img img {
      width: 100%;
      display: block;
      object-fit: cover;
      border-radius: 12px;
      opacity: 0.85;
    }
    .login-panel-brands {
      border-top: 1px solid rgba(255,255,255,0.08);
      padding-top: 24px;
    }
    .login-brands-label {
      font-size: 11px;
      font-weight: 600;
      letter-spacing: .08em;
      text-transform: uppercase;
      color: rgba(255,255,255,0.35);
      margin-bottom: 16px;
    }
    .login-brands-grid {
      display: flex;
      gap: 20px;
      flex-wrap: wrap;
      align-items: center;
    }
    .login-brands-grid img {
      height: 20px;
      filter: brightness(0) invert(1);
      opacity: 0.4;
    }

    /* ── Right / form side ── */
    .login-form-side {
      flex: 1;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 48px 32px;
      overflow-y: auto;
    }
    .login-form-card {
      width: 100%;
      max-width: 400px;
    }
    .login-form-header {
      margin-bottom: 32px;
    }
    .login-form-eyebrow {
      font-size: 11px;
      font-weight: 600;
      letter-spacing: .1em;
      text-transform: uppercase;
      color: ${tokens.accent};
      margin-bottom: 8px;
    }
    .login-form-title {
      font-size: 26px;
      font-weight: 700;
      color: ${tokens.textPrimary};
      margin: 0 0 6px;
    }
    .login-form-sub {
      font-size: 14px;
      color: ${tokens.textSecondary};
    }

    /* MUI TextField enterprise overrides */
    .login-form-card .MuiOutlinedInput-root {
      border-radius: 8px !important;
      font-size: 14px !important;
      background: ${tokens.white} !important;
      font-family: 'Inter', system-ui, sans-serif !important;
    }
    .login-form-card .MuiOutlinedInput-notchedOutline {
      border-color: ${tokens.border} !important;
    }
    .login-form-card .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline {
      border-color: #94A3B8 !important;
    }
    .login-form-card .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline {
      border-color: ${tokens.accent} !important;
      border-width: 1.5px !important;
    }
    .login-form-card .MuiFormLabel-root {
      font-family: 'Inter', system-ui, sans-serif !important;
      font-size: 14px !important;
    }
    .login-form-card .MuiFormLabel-root.Mui-focused {
      color: ${tokens.accent} !important;
    }

    /* Error banner */
    .login-error-banner {
      background: #FEF2F2;
      border: 1px solid #FEE2E2;
      color: #991B1B;
      border-radius: 8px;
      padding: 10px 14px;
      font-size: 13px;
      font-weight: 500;
      text-align: center;
    }

    /* Submit button */
    .login-submit-btn {
      width: 100% !important;
      height: 44px !important;
      background: ${tokens.accent} !important;
      color: #fff !important;
      font-size: 14px !important;
      font-weight: 600 !important;
      letter-spacing: .02em !important;
      border-radius: 8px !important;
      text-transform: none !important;
      box-shadow: none !important;
      font-family: 'Inter', system-ui, sans-serif !important;
      transition: background .15s !important;
    }
    .login-submit-btn:hover {
      background: ${tokens.accentHover} !important;
    }

    /* Footer */
    .login-footer {
      margin-top: 28px;
      padding-top: 24px;
      border-top: 1px solid ${tokens.border};
      text-align: center;
      font-size: 13px;
      color: ${tokens.textSecondary};
      font-weight: 500;
    }
    .login-footer a {
      color: ${tokens.accent};
      font-weight: 700;
      text-decoration: none;
    }
    .login-footer a:hover { text-decoration: underline; }

    /* Mobile */
    @media (max-width: 768px) {
      .login-panel { display: none; }
      .login-form-side { padding: 32px 16px; }
    }
  `}</style>
);

// ─── Component ────────────────────────────────────────────────────────────────
const LoginPage = () => {
  const auth = useSelector(state => state.auth);
  const dispatch = useDispatch();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setErrors] = useState(null);
  const [emailerror, setemailErrors] = useState(null);
  const [passworderror, setpasswordErrors] = useState(null);
  const [visible, SetVisible] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const handleClickShowPassword = () => setShowPassword(!showPassword);
  const handleMouseDownPassword = () => setShowPassword(!showPassword);

  const validateerror = document.getElementsByClassName("validateErrors");
  if (validateerror) {
    setTimeout(function () {
      SetVisible(true);
    }, 5000);
  }

  const handleSubmit = () => {
    if (email != '' && password != '') {
      const values = { 'email': email, 'password': password };
      dispatch(doLogin(values));
    } else {
      setErrors("All the fields must be entered");
    }
  };

  const emailValidate = () => {
    const emailValidation = !/\S+@\S+\.\S+/.test(email);
    if (emailValidation) {
      setemailErrors("Email address is invalid");
    } else {
      setemailErrors(null);
    }
  };

  const passwordValidate = () => {
    if (password.length < 7) {
      setpasswordErrors("Password should be eight characters");
    } else {
      setpasswordErrors(null);
    }
  };

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <>
      <GlobalStyles />

      <div className="login-root">

        {/* ── Left panel ── */}
        <aside className="login-panel">
          <div className="login-panel-logo">
            <a href="/">
              <img src="/assets/images/shippingchimp-logo-white.png" alt="ShippingChimp" />
            </a>
          </div>

          <div>
            <div className="login-panel-badge">
              <svg width="8" height="8" viewBox="0 0 8 8" fill="none">
                <circle cx="4" cy="4" r="4" fill="#60A5FA" />
              </svg>
              Trusted by 500+ brands
            </div>
            <h2 className="login-panel-headline">
              Smarter shipping,<br />faster delivery.
            </h2>
            <p className="login-panel-quote">
              ShippingChimp beats all the major larger shipping services out there. Their app works great with our Shopify store and everything integrates easily.
            </p>

            {/* Register image reused */}
            <div className="login-panel-img">
              <img src="/assets/images/register-img.png" alt="ShippingChimp platform" />
            </div>

            <div className="login-panel-stats">
              <div>
                <div className="login-stat-num">99.4%</div>
                <div className="login-stat-label">On-time delivery</div>
              </div>
              <div>
                <div className="login-stat-num">2-min</div>
                <div className="login-stat-label">Setup time</div>
              </div>
              <div>
                <div className="login-stat-num">24/7</div>
                <div className="login-stat-label">Support</div>
              </div>
            </div>
          </div>

          <div className="login-panel-brands">
            <p className="login-brands-label">Trusted by Canadian brands</p>
            <div className="login-brands-grid">
              <img src="/assets/images/tawse.png" alt="Tawse" />
              <img src="/assets/images/sleepcountry.png" alt="Sleep Country" />
              <img src="/assets/images/hatley.png" alt="Hatley" />
              <img src="/assets/images/homedics.png" alt="HoMedics" />
            </div>
          </div>
        </aside>

        {/* ── Form side ── */}
        <main className="login-form-side">
          <div className="login-form-card">

            <div className="login-form-header">
              <p className="login-form-eyebrow">Welcome back</p>
              <h1 className="login-form-title">Sign in to your account</h1>
              <p className="login-form-sub">Enter your credentials to continue.</p>
            </div>

            <Stack
              component="form"
              sx={{ width: "100%", gap: 2.5 }}
              Validate
              autoComplete="off"
            >
              {/* Global validation banner */}
              {!visible && error && (
                <div className="login-error-banner validateErrors">{error}</div>
              )}

              <TextField
                id="email"
                label="Email Address"
                size="small"
                required
                variant="outlined"
                onInput={e => setEmail(e.target.value)}
                onBlur={emailValidate}
                error={!!emailerror}
                helperText={emailerror}
                fullWidth
                sx={{
                  '& .MuiFormHelperText-root': { mx: 0.5, mt: 0.75, fontWeight: 500 }
                }}
              />

              <TextField
                id="password"
                label="Password"
                size="small"
                onBlur={passwordValidate}
                required
                type={showPassword ? "text" : "password"}
                variant="outlined"
                value={password}
                error={!!passworderror}
                helperText={passworderror}
                onInput={e => setPassword(e.target.value)}
                onKeyUp={(event) => {
                  if (event.key == 'Enter') handleSubmit();
                }}
                fullWidth
                sx={{
                  '& .MuiFormHelperText-root': { mx: 0.5, mt: 0.75, fontWeight: 500 }
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleClickShowPassword}
                        onMouseDown={handleMouseDownPassword}
                        edge="end"
                        sx={{ color: '#64748B' }}
                      >
                        {showPassword
                          ? <Visibility sx={{ fontSize: '18px' }} />
                          : <VisibilityOff sx={{ fontSize: '18px' }} />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />

              {/* Forgot password */}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: -1 }}>
                <Box
                  component="a"
                  href="/passwordreset"
                  sx={{
                    fontSize: '13px',
                    fontWeight: 600,
                    color: tokens.accent,
                    textDecoration: 'none',
                    '&:hover': { textDecoration: 'underline' }
                  }}
                >
                  Forgot password?
                </Box>
              </Box>

              {/* Redux API error — with .data */}
              {!visible && auth.notification.messageContent && auth.notification.messageContent.data && (
                <div className="login-error-banner validateErrors">
                  {auth.notification.messageContent.data.message}
                </div>
              )}

              {/* Redux API error — without .data */}
              {!visible && auth.notification.messageContent && !auth.notification.messageContent.data && (
                <div className="login-error-banner validateErrors">
                  {auth.notification.messageContent}
                </div>
              )}

              <Button
                variant="contained"
                onClick={handleSubmit}
                className="login-submit-btn"
              >
                Sign in →
              </Button>
            </Stack>

            {/* Footer */}
            <div className="login-footer">
              Don't have an account?{' '}
              <a href="/register">Create one free</a>
            </div>

          </div>
        </main>

      </div>
    </>
  );
};

export default LoginPage;